import {BlockchainVersion} from '@/model/resource/BlockchainVersion'
import {BlockchainVersionCollection} from '@/model/collection/BlockchainVersionCollection'
import {Contract} from '@/model/resource/Contract'
import {ContractCollection} from '@/model/collection/ContractCollection'
import {TokenPriced} from '@/model/resource/TokenPriced'
import {TokenPricedCollection} from '@/model/collection/TokenPricedCollection'

/**
 * The two networks Orbit runs on, as the CMS lists show and filter them.
 *
 * The identifier is `blockchain_version.networkIdentifier` — the very same string
 * the network bar resolves to (see {@link currentN3Network}), so a row's network
 * and the network the CMS is working against are always comparable.
 */
export const ORBIT_NETWORK_MAINNET = 'neo3/mainnet'
export const ORBIT_NETWORK_TESTNET = 'neo3/testnet'

export const NATIVE_N3_TOKEN_HASHES = [
  '0xef4073a0f2b305a38ec4050e4d3d28bc40ea63f5', // NEO
  '0xd2a4cff31913016155e38e474a2c06d08be276cf', // GAS
]

/** Option shape InputSelect binds to (it needs objects, not scalars). */
export type OrbitNetworkOption = {$id: string; $tag: string}

const LABELS: Record<string, string> = {
  [ORBIT_NETWORK_MAINNET]: 'N3 MainNet',
  [ORBIT_NETWORK_TESTNET]: 'N3 TestNet',
}

export class OrbitNetworkHelper {
  /** blockchain_version rows are static, so one lookup per network is enough. */
  private static blockchainVersionCache = new Map<string, BlockchainVersion>()

  /**
   * The `blockchain_version` row of a network — what every Orbit record is stamped
   * with. Throws when the network has no row: writing the wrong network's id, or
   * none at all, is worse than not writing.
   */
  static async blockchainVersionOf(
    networkIdentifier: string
  ): Promise<BlockchainVersion> {
    const cached = this.blockchainVersionCache.get(networkIdentifier)
    if (cached) return cached

    const collection = new BlockchainVersionCollection()
    collection.networkIdentifier = networkIdentifier
    await collection.queryAsPage()

    const blockchainVersion = collection.items?.[0]

    if (!blockchainVersion) {
      throw new Error(`No blockchain_version row for ${networkIdentifier}`)
    }

    this.blockchainVersionCache.set(networkIdentifier, blockchainVersion)

    return blockchainVersion
  }

  static isNativeTokenHash(hash?: string | null): boolean {
    if (!hash) return false

    return NATIVE_N3_TOKEN_HASHES.includes(hash.trim().toLowerCase())
  }

  static referenceNetworkOf(networkIdentifier: string): string {
    return this.isTestnet(networkIdentifier)
      ? ORBIT_NETWORK_MAINNET
      : networkIdentifier
  }

  static async contractOptionsOf(
    networkIdentifier: string
  ): Promise<Contract[]> {
    const reference = this.referenceNetworkOf(networkIdentifier)

    const networks =
      reference === networkIdentifier
        ? [networkIdentifier]
        : [networkIdentifier, reference]

    const lists = await Promise.all(
      networks.map(network => this.contractsOfNetwork(network))
    )

    const byKey = new Map<string, Contract>()

    lists.forEach(list =>
      list.forEach(contract => {
        const key =
          contract.hash?.trim().toLowerCase() || String(contract.idContractPk)

        if (!byKey.has(key)) byKey.set(key, contract)
      })
    )

    return Array.from(byKey.values())
  }

  private static async contractsOfNetwork(
    networkIdentifier: string
  ): Promise<Contract[]> {
    let blockchainVersion: BlockchainVersion

    try {
      blockchainVersion = await this.blockchainVersionOf(networkIdentifier)
    } catch {
      return []
    }

    const collection = new ContractCollection()
    collection.active = null
    collection.limit = 9999
    collection.idBlockchainVersionFk = [blockchainVersion.idBlockchainVersionPk]
    await collection.expand()

    return collection.items || []
  }

  static async paymentTokenOptionsOf(
    networkIdentifier: string
  ): Promise<TokenPriced[]> {
    const byHash = await this.paymentTokenMapOf(
      networkIdentifier,
      NATIVE_N3_TOKEN_HASHES
    )

    const own = await this.tokensOfNetwork(networkIdentifier, null)
    own.forEach(token => {
      if (token.hash) byHash.set(token.hash.trim().toLowerCase(), token)
    })

    return Array.from(byHash.values())
  }

  static async paymentTokenMapOf(
    networkIdentifier: string,
    hashes: string[]
  ): Promise<Map<string, TokenPriced>> {
    const wanted = Array.from(
      new Set(hashes.filter(Boolean).map(hash => hash.trim().toLowerCase()))
    )

    const byHash = new Map<string, TokenPriced>()
    if (wanted.length === 0) return byHash

    const own = await this.tokensOfNetwork(networkIdentifier, wanted)
    own.forEach(token => {
      if (token.hash) byHash.set(token.hash.trim().toLowerCase(), token)
    })

    const borrowable = wanted.filter(
      hash => !byHash.has(hash) && this.isNativeTokenHash(hash)
    )
    const reference = this.referenceNetworkOf(networkIdentifier)

    if (borrowable.length === 0 || reference === networkIdentifier) {
      return byHash
    }

    const borrowed = await this.tokensOfNetwork(reference, borrowable)
    borrowed.forEach(token => {
      const hash = token.hash?.trim().toLowerCase()
      if (hash && !byHash.has(hash)) byHash.set(hash, token)
    })

    return byHash
  }

  private static async tokensOfNetwork(
    networkIdentifier: string,
    hashes: string[] | null
  ): Promise<TokenPriced[]> {
    let blockchainVersion: BlockchainVersion

    try {
      blockchainVersion = await this.blockchainVersionOf(networkIdentifier)
    } catch {
      return []
    }

    const collection = new TokenPricedCollection()
    collection.idBlockchainVersionFk = blockchainVersion.idBlockchainVersionPk
    collection.limit = hashes?.length ? hashes.length : 9999

    if (hashes?.length) collection.hashes = hashes

    await collection.queryAsPage()

    return collection.items || []
  }

  /** The only two networks a row can belong to, mainnet first. */
  static get identifiers(): string[] {
    return [ORBIT_NETWORK_MAINNET, ORBIT_NETWORK_TESTNET]
  }

  /** 'neo3/mainnet' -> 'N3 MainNet'. Unknown/missing networks show as N/A. */
  static label(networkIdentifier?: string | null): string {
    if (!networkIdentifier) return 'N/A'

    return LABELS[networkIdentifier.trim().toLowerCase()] ?? networkIdentifier
  }

  static isTestnet(networkIdentifier?: string | null): boolean {
    return networkIdentifier?.trim().toLowerCase() === ORBIT_NETWORK_TESTNET
  }

  /**
   * Select items for the Network filter: the "all" entry (empty id) followed by
   * the two networks. `allLabel` comes from the view so it stays translatable.
   */
  static options(allLabel: string): OrbitNetworkOption[] {
    return [
      {$id: '', $tag: allLabel},
      ...this.identifiers.map(id => ({$id: id, $tag: this.label(id)})),
    ]
  }

  /** Resolves a select value back into the option object InputSelect holds. */
  static optionOf(
    options: OrbitNetworkOption[],
    networkIdentifier: string | null
  ): OrbitNetworkOption | null {
    if (options.length === 0) return null

    return options.find(o => o.$id === (networkIdentifier ?? '')) ?? options[0]
  }

  /**
   * Badge colouring, matching the status badges of the rewards list. Testnet is
   * the one that must never be mistaken for the network holding real money.
   */
  static badgeClass(networkIdentifier?: string | null): string {
    // Tailwind 1.x here: stick to its default palette (no sky/amber/slate).
    const base =
      'inline-block px-3 py-1 rounded-full font-semibold text-sm whitespace-no-wrap'

    if (!networkIdentifier) return `${base} bg-gray-200 text-gray-600`

    return this.isTestnet(networkIdentifier)
      ? `${base} bg-orange-100 text-orange-800`
      : `${base} bg-blue-100 text-blue-800`
  }
}
